import { z } from 'zod';

export const orgWaybillLogoShapeSchema = z.enum(['square', 'rectangle']);

export const orgWaybillLogoBackgroundSchema = z.enum(['white', 'none']);

export const orgWaybillLogoSizeSchema = z.enum(['small', 'medium', 'large']);

export const orgWaybillLogoRoundingSchema = z.enum(['none', 'sm', 'md', 'full']);

export const orgWaybillSettingsSchema = z.object({
  /** Accent color for waybill header band, route strip, and card chips. */
  accent_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),

  /** When false, carrier logo is hidden on the waybill header. */
  use_org_logo: z.boolean().optional(),

  /** Header logo slot aspect ratio — square for icons, rectangle for wide wordmarks. */
  logo_shape: orgWaybillLogoShapeSchema.optional(),

  /** White pill behind the logo, or none (logo sits directly on the accent band). */
  logo_background: orgWaybillLogoBackgroundSchema.optional(),

  /** Header logo slot scale. */
  logo_size: orgWaybillLogoSizeSchema.optional(),

  /** Corner rounding on the logo slot (and clip when on accent). */
  logo_rounding: orgWaybillLogoRoundingSchema.optional(),
});

export type OrgWaybillSettings = z.infer<typeof orgWaybillSettingsSchema>;
export type OrgWaybillLogoShape = z.infer<typeof orgWaybillLogoShapeSchema>;
export type OrgWaybillLogoBackground = z.infer<typeof orgWaybillLogoBackgroundSchema>;
export type OrgWaybillLogoSize = z.infer<typeof orgWaybillLogoSizeSchema>;
export type OrgWaybillLogoRounding = z.infer<typeof orgWaybillLogoRoundingSchema>;

/** Platform defaults for waybill branding — render, forms, and reset. */
export const defaultWaybillSettings = {
  accent_color: '#2563EB',
  use_org_logo: false,
  logo_shape: 'rectangle',
  logo_background: 'white',
  logo_size: 'medium',
  logo_rounding: 'md',
} as const satisfies Required<
  Pick<
    OrgWaybillSettings,
    'use_org_logo' | 'logo_shape' | 'logo_background' | 'logo_size' | 'logo_rounding'
  >
> & { accent_color: string };

export type DefaultWaybillSettings = typeof defaultWaybillSettings;

/** @deprecated Prefer {@link defaultWaybillSettings} */
export const ORG_WAYBILL_SETTINGS_DEFAULTS = defaultWaybillSettings;
